import { useAuth } from "../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { apiClient } from "../api/api";
import React, { useState, useEffect } from "react";
import { Eye, EyeOff, Mail, Lock, Check } from "lucide-react";
import "./Login.css";

const Login = () => {
  const [form, setForm] = useState({ username: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { setIsAuthenticated, login } = useAuth();
  const [activationMessage, setActivationMessage] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("activated") === "true") {
      setActivationMessage("Account activated successfully! You can now sign in.");
    } else if (params.get("error") === "invalid_token") {
      setError("Invalid or expired activation link.");
    } else if (params.get("error") === "activation_failed") {
      setError("Failed to activate account. Please try again or contact support.");
    } else if (params.get("error") === "server_error") {
      setError("A server error occurred during activation.");
    }
  }, [location]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    console.log("[Login] Form submitted:", form);
    // Log credentials being sent (for dev only!)
    console.log("[Login] Sending credentials:", {
      email: form.email,
      password: form.password,
    });

    // Send credentials to backend '/login' endpoint to set cookie and validate
    try {
      console.log("API HHit for logon");
      const loginResp = await apiClient.post(
        "/login",
        { email: form.email, password: form.password },
        { withCredentials: true, credentials: "include" }
      );
      console.log("[Login] Received backend response:", loginResp.data);
      if (loginResp.data.status !== "ok") {
        setError("Invalid credentials. Please try again.");
        setLoading(false);
        return;
      }
      // Construct user object
      const user = loginResp.data.user || {
          id: loginResp.data.id,
          email: loginResp.data.email,
          role: loginResp.data.role,
          brand_id: loginResp.data.brand_id,
          company_type: loginResp.data.company_type
      };

      // Save userId immediately
      const userId = loginResp.data.id || user.id;
      if (userId) {
          localStorage.setItem("userId", userId);
          console.log("[Login] userId saved to localStorage:", userId);
      }

      const role = user.role || loginResp.data.role;
      if (role === "super_admin") {
        login(user);
        navigate("/admin");
        setLoading(false);
        return;
      }

      // Use login() to update context with user data immediately
      login(user);
      
      const redirectUrl = loginResp.data.redirect_url || "/dashboard";
      console.log("[Login] Authenticated, navigating to:", redirectUrl);
      navigate(redirectUrl);
    } catch (err) {
      console.error("[Login] Backend /login error:", err);
      setError("Invalid credentials. Please try again.");
    }
    setLoading(false);
    console.log("[Login] Login process complete.");
  };

  return (
    <div className="login-container">
      {/* --- Left Side (Marketing) --- */}
      <div className="login-left">
        <div className="marketing-content">
          <div className="brand-login-logo">
            shop<span>OS</span>.
          </div>
          <h1 className="tagline">
            Key metrics to grow revenue – all in one place
          </h1>
        </div>
      </div>

      {/* --- Right Side (Login Form) --- */}
      <div className="login-right">
        <div className="login-box">
          <div className="login-header">
            <h1>Welcome back</h1>
            <p>Enter your credentials to access your account</p>
          </div>

          {error && <div className="error-message">{error}</div>}
          {activationMessage && (
            <div 
              className="error-message" 
              style={{ 
                color: "#00c853", 
                borderColor: "#00c853", 
                background: "rgba(0,200,83,0.1)" 
              }}
            >
              {activationMessage}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Email Address</label>
              <div className="input-wrapper">
                <Mail className="input-icon" size={18} />
                <input
                  type="email"
                  name="email"
                  placeholder="john@example.com"
                  value={form.email}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Password</label>
              <div className="input-wrapper">
                <Lock className="input-icon" size={18} />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Enter your password"
                  value={form.password}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="form-actions">
              <label className="checkbox-container">
                <input type="checkbox" />
                <span className="custom-checkbox">
                  <Check size={12} strokeWidth={3} color="#fff" />
                </span>
                <span className="checkbox-label">Remember me</span>
              </label>
              <a href="#" className="forgot-password">
                Forgot password?
              </a>
            </div>

            <button type="submit" className="login-button" disabled={loading}>
              {loading ? "Logging in..." : "Sign In"}
            </button>
          </form>

          <div className="signup-link">
            Don't have an account?
            <a href="/signup">Sign Up</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
